// Pure helpers for the "Oficinas"/"Alertas" cierre views (the redesign ported from
// mantis-business-front-brinks). Shapes real `filterOffices`/`getCierreAlerts` rows into what
// those views render — no fetching, no React, so this is testable without a DOM or a backend.
//
// `SEVERITY_TYPES` comes from `cierreClosingDetail.js` and `STATUS_LABELS` from
// `cierreCorrections.js` rather than being re-declared, so the two views (Centro de Control's
// process panel and this one) never disagree about which severities block or about what "resolved
// by a person" is called.

import { formatMoney } from "./formatMoney";
import { SEVERITY_TYPES, isCuadreExitoso } from "./cierreClosingDetail";
import { STATUS_LABELS } from "./cierreCorrections";
import { getMonthAbreviation, parseDateOnly } from "./dateUtils";

// `panel/clients/<code>.yaml`'s two-value vocabulary. A third UI-only tone ("bloqueado") is
// derived below from a real, non-zero `diferencia_cuadre` — the panel itself has no third state,
// so this is a display heuristic, never a fact written anywhere.
export const CUADRE_OK_STATUS = "Good to Go!";

// `null`/`undefined` -> "—", never "$0" — a missing figure is not a zero figure (locked
// decision #7, carried into the panel projection and preserved here).
export function moneda(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${formatMoney(Math.abs(n))}`;
}

// `projection/rows.js`'s `mainKeyFor`: `CIERRE <officeName> <DD-MM-YYYY>`. Only ever built there
// and read back apart here for display — never re-parsed into a date.
export function nombreOficinaDeMainKey(mainKey) {
  if (!mainKey) return "—";
  return String(mainKey)
    .replace(/^CIERRE\s+/i, "")
    .replace(/\s+\d{2}-\d{2}-\d{4}$/, "")
    .trim();
}

// `fecha_cuadre` arrives `YYYY-MM-DD` (BR-04's business_date, never a constructed `Date` — see
// `projection/rows.js`'s own `toDMY`). Same move, string to string.
export function fechaDMY(fechaCuadre) {
  if (!fechaCuadre) return "—";
  const [y, m, d] = String(fechaCuadre).split("T")[0].split("-");
  if (!y || !m || !d) return String(fechaCuadre);
  return `${d}-${m}-${y}`;
}

// A document/source key as a person reads it: `cuadre_oficina` -> `Cuadre Oficina`. Display only —
// every lookup, request and comparison still runs on the key itself, so this only ever changes what
// is drawn beside a value, never the value. The raw key stays on screen next to it (B-45).
//
// A uuid passes through untouched. `toStepView` labels a source `sourceKey || id`, so a source that
// never declared a key reaches this helper as a bare uuid — title-casing one would dress an opaque
// id up as a name, which is worse than showing it plainly.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function nombreLegibleDeClave(key) {
  const crudo = String(key ?? "").trim();
  if (!crudo || UUID.test(crudo)) return crudo;
  return crudo.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// The documents an alert cites, each with the file behind it — B-45.
//
// The profile author lists these per alert as "the documents a person should open to investigate",
// and they have always been on the wire as `evidenceSources`: bare keys, with no file name. The
// backend now resolves them to `evidenceDocuments`, so a key that has a document arrives with the
// real name of the file somebody uploaded.
//
// `evidenceSources` is the fallback, and it is not dead code — the frontend deploys separately
// from the backend, so a window exists where this build talks to a backend that has not shipped
// the resolution yet. In that window a document shows its readable name and no file beneath it,
// which is what it looked like before this change.
//
// `archivo` is null for a cited document nobody has uploaded. That is not an error to hide: a
// blocking alert usually IS "this document is missing", so the row stays and the caller decides
// how to say so.
export function documentosDeAlerta(alert) {
  const citados = alert?.evidenceDocuments;
  if (Array.isArray(citados)) {
    return citados
      .filter((doc) => doc && doc.sourceKey)
      .map((doc) => ({ sourceKey: doc.sourceKey, archivo: archivoDeDocumento(doc) }));
  }

  return (alert?.evidenceSources || [])
    .filter(Boolean)
    .map((sourceKey) => ({ sourceKey, archivo: null }));
}

// The file name to show for one document: what a person called it, else what the pipeline called
// it, else nothing. `filename` is `<sourceKey>.<ext>` for every document nobody uploaded by hand,
// which is why the readable name is drawn from the key and this only ever sits beneath it.
export function archivoDeDocumento(doc) {
  return doc?.originalFilename || doc?.filename || null;
}

// A panel row's operator-facing state, collapsed to the three tones the redesign's EstadoBadge
// draws. `status` is the client's own two-value label; "bloqueado" is a display-only third tone
// for a row that is NOT balanced AND carries a real, resolved, non-zero `diferencia_cuadre` — the
// same heuristic the brinks mockup used, reproduced here against the real field instead of a
// fixture. A `null`/absent difference (unresolved, not zero) never counts as blocked.
//
// B-62 — with `alertas` (the closing's alerts, from `getCierreAlertsBatch`) the decision is the
// detail's own: an OPEN alert of the "ALERT" severity bucket blocks, whatever the client. That is
// what makes Bancamía's caja/bóveda differences read "bloqueado" — it has no `diferencia_cuadre`.
// Without `alertas` it keeps the old Davivienda heuristic, for the views not yet passing them.
// B-83 — only a DESCUADRE blocks: an open alert of severity "alert", figures that disagree. An
// open "blocked" alert ("No se pudo revisar todavía. Falta en Bóveda: Saldo inicial de bóveda.")
// says a value has not arrived yet; it used to count as "Bloqueado" too, so every Bancamía office
// waiting for its opening balance read as blocked (140 of 140) and the cards said nothing.
// Missing information is "pendiente" — the "Pendientes" column says what is missing.
export const esDescuadreAbierto = (a) => a?.status === "OPEN" && a?.severity === "alert";

// B-83 — the alerts a person still has to look at. Attending an alert does not make the figures
// agree, so the next evaluation raises the same alert again, seconds later (`lifecycle.js` only
// matches OPEN rows — by design, see mantis/docs/CIERRES_CORRECTIONS.md). To the operator that read
// as "I attended it and it is still there". An OPEN alert saying exactly what one already attended
// by a person said — same rule, same message, so the same amount — is that same difference:
// it is left out here and the attended one stands for it. A different amount is news, and stays.
const claveDeAlerta = (a) => `${a?.ruleKey || ""}|${a?.message || ""}`;

// B-80 — an alert attended before its closing was redone (`discarded`) no longer stands for anything.
export const esAtendidaVigente = (a) => a?.status === "RESOLVED_BY_USER" && !a?.discarded;

export function alertasVigentes(alertas) {
  if (!Array.isArray(alertas)) return alertas;
  const atendidas = new Set(alertas.filter(esAtendidaVigente).map(claveDeAlerta));
  if (!atendidas.size) return alertas;
  return alertas.filter((a) => !(a?.status === "OPEN" && atendidas.has(claveDeAlerta(a))));
}

export function estadoDeFila(row, alertas) {
  if (row?.status === CUADRE_OK_STATUS) return "cuadre";
  if (Array.isArray(alertas)) {
    return alertasVigentes(alertas).some(esDescuadreAbierto) ? "bloqueado" : "pendiente";
  }
  const diff = row?.diferencia_cuadre;
  if (diff !== null && diff !== undefined && diff !== "" && Number(diff) !== 0) {
    return "bloqueado";
  }
  return "pendiente";
}

// B-62 — the list's figure columns are the client's own: the `resumen` group of its panel config
// (`panel/clients/<code>.yaml`, served by `getExtractionFields` as `fields`). Davivienda's is
// diferencia_cuadre + diferencia_doe (the old "Cuadre total"/"Cuadre DOE"); Bancamía's is
// diferencia_caja + diferencia_boveda. A client with no `resumen` group shows no figure column
// rather than another client's.
//
// A client whose list is the whole CUADRE (Bancamía) keeps each difference in its column
// position and marks it `summary: true` instead of grouping it under `resumen`; those marked
// fields are its resumen, in order.
export function columnasDeResumen(fields) {
  const marcadas = Object.values(fields || {})
    .flat()
    .filter((c) => c?.summary === true);
  if (marcadas.length) return marcadas.map(columnaDeCampo);
  const resumen = fields?.resumen;
  return Array.isArray(resumen) ? resumen.map(columnaDeCampo) : [];
}

// One served field as a list column. `formato` is the backend's `format`: "count" for a number of
// transactions, "money" (also when absent, for a backend that predates it) for pesos.
// `excel` is the served `export` — the column's heading, colours and number format in the
// downloaded Matriz (`utils/cierreExcel.js`) — and only present when the config declares one.
function columnaDeCampo(c) {
  return {
    fieldName: c.fieldName,
    etiqueta: c.friendlyName || c.fieldName,
    formato: c.format === "count" ? "count" : "money",
    ...(c.export ? { excel: c.export } : {}),
  };
}

// The list's columns. `list` is `getExtractionFields`' own: "all" draws every group, in the order
// the panel config declares them (Bancamía's CUADRE, E to AJ); anything else, absent included,
// keeps the resumen columns alone — what Davivienda's list has always shown.
export function columnasDeLista(fields, list) {
  if (list !== "all") return columnasDeResumen(fields);
  return Object.values(fields || {})
    .flat()
    .filter((c) => c?.fieldName)
    .map(columnaDeCampo);
}

// A cell as the list prints it: a count as a plain integer, pesos through `moneda`. Absent is
// "—" either way, never a zero.
export function valorDeCelda(value, formato) {
  if (formato !== "count") return moneda(value);
  const n = numeroDeCelda(value);
  return n === null ? "—" : formatMoney(n);
}

// The CUADRE's AK OBSERVACIONES for one closing: the notes people wrote when they attended its
// alerts (`closing_alerts.resolution_note`), each after its alert's name — a closing can carry a
// caja, a bóveda and an anulación alert, each with its own note. Null when nobody wrote one.
//
// Only what a PERSON wrote (B-107): the note of an alert someone attended, or the reason of the
// correction that resolved it (B-116). An alert the data resolved or replaced also carries a
// `resolutionNote`, the engine's ("Reemplazada en la evaluación … por …"), which nobody wrote and
// which filled the column with every rule of every evaluation. The backend's Matriz and the day's
// email (`readObservations`) take the same rule.
export function notaDePersona(alerta) {
  // B-80 — the observation on an alert attended before a redo no longer counts.
  if (!alerta || alerta.discarded) return "";
  if (alerta.correctedBy) return String(alerta.correctionReason || "").trim();
  if (alerta.status === "RESOLVED_BY_USER") return String(alerta.resolutionNote || "").trim();
  return "";
}

export function observacionesDeAlertas(alertas, nombrar = nombreLegibleDeClave) {
  const notas = (alertas || [])
    .map((a) => ({ a, nota: notaDePersona(a) }))
    .filter(({ nota }) => nota !== "")
    .map(({ a, nota }) => `${nombrar(a.ruleKey)}: ${nota}`);
  return notas.length ? notas.join(" · ") : null;
}

// A panel cell as a number, or null — absent is never zero.
export function numeroDeCelda(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// The closings panel and the per-office detail only: an amount that is not a whole peso keeps
// its cents. `moneda` rounds to the peso, so a $0,40 difference read "$0" — in red, because the
// cell is red whenever the value is not zero. Whole amounts print exactly as `moneda` does.
// Kept apart from `moneda` on purpose: the Alertas feed and the other screens use that one.
export function montoLegible(value) {
  const n = numeroDeCelda(value);
  if (n === null) return "—";
  const conCentavos = Math.round(Math.abs(n) * 100) % 100 !== 0;
  return `${n < 0 ? "-" : ""}$${formatMoney(Math.abs(n), conCentavos ? 2 : 0)}`;
}

// `valorDeCelda`, with `montoLegible` for amounts: a count prints as before.
export const valorLegible = (value, formato) => (formato === "count" ? valorDeCelda(value, formato) : montoLegible(value));

// A captured value in the detail's "Datos extraídos": formatted like the panel's own cell when it
// is a number (every panel field is "money" or "count"), as it came otherwise.
export function datoLegible(value, formato) {
  if (value === null || value === undefined || value === "") return "—";
  return numeroDeCelda(value) === null ? String(value) : valorLegible(value, formato === "count" ? "count" : "money");
}

// What a closing is waiting on, from its alerts (the panel's one `getCierreAlertsBatch`), split
// the way an operator reads it:
//   - `faltan`: the blocked alerts — "No se pudo revisar todavía. Falta en Bóveda: Saldo inicial de
//     bóveda." A value or a document has not arrived, so the rule could not be checked. Most are
//     not documents (Bancamía's opening vault balance is a carried value), which is why the
//     column says WHAT is missing rather than "documento faltante".
//   - `descuadres`: the open alerts whose figures disagree.
// `mensajes` are the alerts' own sentences (old blocked ones rewritten by `traducir`), for the
// tooltip. Null until the batch has answered.
const FALTA_EN = /Falta en ([^:]+):\s*([^.]+)\./g;

// B-132 — the documents an open `documentos_faltantes` says the office has not sent, in its own
// words ("No envió: Arqueo manual firmado, Notas contables 55 y 14"). Split on commas only: a
// document's name may carry a "y". Whatever its severity — Banco Agrario's is a subalert, for
// review — a document still to arrive is pending, and the operator reads it where pending is said.
const NO_ENVIO = /^\s*No envió:\s*(.+?)\.?\s*$/;
export const FUENTE_DOCUMENTOS = "Documentos del cierre";

export function documentosPendientes(alertas) {
  if (!Array.isArray(alertas)) return [];
  const items = new Set();
  alertasVigentes(alertas)
    .filter((a) => a.status === "OPEN" && a.ruleKey === "documentos_faltantes" && a.severity !== "info")
    .forEach((a) => {
      const lista = NO_ENVIO.exec(String(a.message || ""))?.[1];
      (lista || "").split(/,\s*/).map((x) => x.trim()).filter(Boolean).forEach((x) => items.add(x));
    });
  return [...items];
}

export function pendientesDeAlertas(alertas, traducir = (m) => m) {
  if (!Array.isArray(alertas)) return null;
  const abiertas = alertasVigentes(alertas).filter((a) => a.status === "OPEN");
  const bloqueadas = abiertas.filter((a) => a.severity === "blocked");
  const descuadres = abiertas.filter((a) => a.severity === "alert" && a.ruleKey !== "documentos_faltantes");
  const documentos = documentosPendientes(abiertas);
  const avisoDocumentos = abiertas.filter((a) => a.ruleKey === "documentos_faltantes" && a.severity !== "info");
  // B-132 — what is left for a person to review (a subalert), so a closing in review never reads "Al día".
  const revisiones = abiertas.filter((a) => a.severity === "subalert" && a.ruleKey !== "documentos_faltantes");
  const porFuente = new Map();
  const mensajesFaltan = [
    ...bloqueadas.map((a) => traducir(a.message || "")),
    ...avisoDocumentos.map((a) => a.message || ""),
  ];
  mensajesFaltan.forEach((m) => {
    for (const [, fuente, lista] of m.matchAll(FALTA_EN)) {
      const items = porFuente.get(fuente.trim()) || new Set();
      lista.split(/,\s*|\s+y\s+/).map((x) => x.trim()).filter(Boolean).forEach((x) => items.add(x));
      porFuente.set(fuente.trim(), items);
    }
  });
  const faltan = [...porFuente.entries()].map(([fuente, items]) => ({ fuente, items: [...items] }));
  if (documentos.length) faltan.unshift({ fuente: FUENTE_DOCUMENTOS, items: documentos });
  return {
    faltantes: bloqueadas.length + documentos.length,
    descuadres: descuadres.length,
    revisiones: revisiones.length,
    faltan,
    mensajesFaltan,
    mensajesDescuadre: descuadres.map((a) => a.message || ""),
    mensajesRevision: revisiones.map((a) => a.message || ""),
  };
}

// B-175 — the Matriz's OBSERVACIONES, the report that goes to the client: only what the TEAM wrote
// when attending each alert, or the reason of the correction that resolved it (`notaDePersona`), as
// written — no alert name and never the engine's sentences ("Descuadre en caja de …", "Falta en …"),
// which B-108 put there and the client cannot send on. The same note twice is said once, several
// are joined with " · ". Null when nobody wrote one. `matrix.js#teamNotesOf` in the backend is the
// same rule.
export function notasDelEquipo(alertas) {
  const notas = (alertas || []).map(notaDePersona).filter((nota) => nota !== "");
  return notas.length ? [...new Set(notas)].join(" · ") : null;
}

// The cell's short words for what is missing: the one value when only one is ("Saldo inicial de
// bóveda"), the source when one source lacks several ("Movimientos de caja"), the first source and
// how many more otherwise. "Información" when no sentence names anything.
export function etiquetaDeFaltantes(faltan) {
  if (!faltan || !faltan.length) return "Falta información";
  if (faltan.length === 1) {
    const [{ fuente, items }] = faltan;
    return `Falta: ${items.length === 1 ? items[0] : fuente}`;
  }
  return `Falta: ${faltan[0].fuente} y ${faltan.length - 1} más`;
}

// What one row adds to "Discrepancia total": the sum of the absolute values of its resumen
// columns, so a sobrante and a faltante never cancel out. Null when none of them resolved.
export function discrepanciaDeFila(row, columnas) {
  const valores = (columnas || []).map((c) => numeroDeCelda(row?.[c.fieldName])).filter((v) => v !== null);
  return valores.length ? valores.reduce((total, v) => total + Math.abs(v), 0) : null;
}

// One `filterOffices` row as the panel draws it, and as the Matriz download writes it. Here rather
// than in `PanelCierres.jsx` because "Descargar Matriz del día" (`AvanceDelDiaDialog.jsx`) writes
// the same rows, and a second copy would be a second Matriz.
//
// `columnas` are the list's columns and `resumen` the client's differences (the same columns
// unless the list is the whole CUADRE); `alertasPorCierre` closingId -> alerts, or null while the
// batch has not answered (then the old heuristic decides, rather than every row reading
// "pendiente" for a moment). `nombrar` names an alert for the observations column.
export function filaDeCierre(row, columnas = [], resumen = [], alertasPorCierre = null, nombrar = undefined) {
  const closingId = row.closing_id || null;
  const valores = columnas.map((c) => numeroDeCelda(row[c.fieldName]));
  const diferencias = resumen.map((c) => numeroDeCelda(row[c.fieldName]));
  const alertas = alertasPorCierre ? alertasPorCierre[closingId] || [] : undefined;
  return {
    id: row.closing_id || `${row.codigo_oficina}-${row.fecha_cuadre}`,
    closingId,
    codigo: row.codigo_oficina || "—",
    oficinaId: row.codigo_oficina || "—",
    oficina: nombreOficinaDeMainKey(row.main_key),
    fecha: row.fecha_cuadre || "",
    fechaLabel: fechaDMY(row.fecha_cuadre),
    regional: row.region || null,
    valores,
    // The first resumen column is the one the range filter applies to — Davivienda's
    // diferencia_cuadre, Bancamía's diferencia_caja.
    diferencia: diferencias.length ? diferencias[0] : null,
    discrepancia: discrepanciaDeFila(row, resumen),
    estado: estadoDeFila(row, alertas),
    observaciones: observacionesDeAlertas(alertas, nombrar),
    // B-175 — the team's notes alone, for the Matriz that goes to the client (`hojaDeMatriz`).
    notasMatriz: notasDelEquipo(alertas),
    raw: row,
  };
}

export const ETIQUETA_ESTADO = {
  cuadre: "Cuadre",
  pendiente: "Pendiente revisión",
  bloqueado: "Bloqueado",
  // An office of the client with no closing yet — one just added from Oficinas ("Añadir oficina").
  sinCierre: "Sin cierres aún",
};

// `getCierreClosingDetail`'s response -> the same three tones, precise where `estadoDeFila`'s
// list-row heuristic is not: an open descuadre (`esDescuadreAbierto`, B-83) is what makes a closing
// "bloqueado" here, not a nonzero-but-unresolved difference nor information still to arrive — the
// list's rule, so the row and its detail always say the same.
export function estadoDeDetalle(detail) {
  if (isCuadreExitoso(detail)) return "cuadre";
  return alertasVigentes(detail?.alerts || []).some(esDescuadreAbierto) ? "bloqueado" : "pendiente";
}

// B-83 — the panel's summary cards are filters too. Whether a row passes the chosen card: a
// state, every row ("todos"), or the rows with some difference ("diferencia", Discrepancia total).
export function pasaTarjeta(fila, tarjeta) {
  if (!tarjeta || tarjeta === "todos") return true;
  if (tarjeta === "diferencia") return (fila?.discrepancia || 0) > 0;
  return fila?.estado === tarjeta;
}

// One row per OFFICE, the most recent closing first — `filterOffices` returns one row per
// CLOSING (BR-23), so the office directory this view wants is a client-side reduction, not a
// second endpoint. Ties on `fecha_cuadre` keep the first (already-sorted) occurrence.
export function ultimoCierrePorOficina(rows) {
  const porOficina = new Map();
  const ordenados = [...(rows || [])].sort((a, b) => {
    const fa = a?.fecha_cuadre || "";
    const fb = b?.fecha_cuadre || "";
    return fa < fb ? 1 : fa > fb ? -1 : 0;
  });
  for (const row of ordenados) {
    const codigo = row?.codigo_oficina;
    if (!codigo || porOficina.has(codigo)) continue;
    porOficina.set(codigo, row);
  }
  return [...porOficina.values()];
}

// `closing_alerts.severity` -> the redesign's three priority buckets. Reuses the same
// ALERT/SUBALERT/INFO split `isCuadreExitoso` gates on, so a row that blocks the closing is
// always "alta" here too.
export function prioridadDeSeveridad(severity) {
  const bucket = SEVERITY_TYPES[severity];
  if (bucket === "ALERT") return "alta";
  if (bucket === "SUBALERT") return "media";
  return "baja";
}

export function esAlertaAbierta(alert) {
  return alert?.status === "OPEN";
}

// B-91 — a priority in the Alertas feed, with the panel's reading of a severity (B-83): only a
// descuadre ("alert") is "alta"; a value that has not arrived yet ("blocked", "No se pudo revisar
// todavía…") is "media", like a "subalert". `prioridadDeSeveridad` stays for the screens that read
// the severity buckets as they were.
export function prioridadEnFeed(severity) {
  if (severity === "alert") return "alta";
  if (severity === "blocked" || severity === "subalert") return "media";
  return "baja";
}

// B-91 — of the rows `listCierreAlerts` serves, the ones the feed shows: the detail's own reading,
// closing by closing — never across closings, where two offices' "Descuadre en caja de $1.501,00"
// are two differences. An attended alert stands for the identical one raised after it
// (`alertasVigentes`), and an attended one shows only while it still counts
// (`esAlertaAtendidaVisible`). Good news ("info") is not an alert. Order is kept.
export function alertasDelFeed(filas) {
  if (!Array.isArray(filas)) return [];
  const porCierre = new Map();
  filas.forEach((a) => {
    if (!porCierre.has(a.closingId)) porCierre.set(a.closingId, []);
    porCierre.get(a.closingId).push(a);
  });
  const vigentes = new Set();
  porCierre.forEach((delCierre) => alertasVigentes(delCierre).forEach((a) => vigentes.add(a)));
  return filas.filter(
    (a) => vigentes.has(a) && a.severity !== "info" && (esAlertaAbierta(a) || esAlertaAtendidaVisible(a))
  );
}

// B-91 — how many alerts each date chip would bring, from the feed's `byDate` (every date of the
// client, counted by the server). The chips ask the server for their range, so they are counted
// before the click from what it already said, not from the alerts on screen.
export function conteosDeFechas(byDate, presets, hoy) {
  return Object.fromEntries(
    presets.map((p) => [
      p.clave,
      (byDate || [])
        .filter((d) => enRangoDeFechas(d.date, rangoDePreset(p.clave, hoy)))
        .reduce((total, d) => total + (d.count || 0), 0),
    ])
  );
}

// An attended alert worth showing under "Ya atendidas". Leaves out the "No se pudo revisar todavía"
// ones the data itself closed: a closing evaluated before its reports arrived (the scheduler, or
// opening it by hand) raises one per rule, and the reports landing resolve them. To the operator
// they were three lines of noise per office saying nothing happened (E2E test, 29/09/2026). A
// blocked alert a PERSON closed is kept — that has a note somebody wrote.
export function esAlertaAtendidaVisible(alert) {
  if (!alert || esAlertaAbierta(alert)) return false;
  // B-80 — attended before the closing was redone: history, not something to look at.
  if (alert.discarded) return false;
  // A difference inherited from an earlier day is never attended here — it goes when its origin is
  // adjusted, and that attention is shown on the origin's own closing.
  if (alert.severity === "carried") return false;
  return !(alert.severity === "blocked" && alert.status === "RESOLVED_BY_DATA");
}

// Blocking (severity "alert") + OPEN alerts on a closing — the "Documentos faltantes" count
// PanelCierres, and now CierresReportes/CierresCalendario, all draw from the same
// `getCierreClosingDetail` response. `null` (not 0) for a closing this fetch hasn't reached yet
// or that has no `closingId` at all (a real closing must exist to ask), so a caller can render
// "…"/"—" rather than a false zero.
export function faltantesDeDetalle(detail) {
  if (!detail) return null;
  return (detail.alerts || []).filter((a) => a.status === "OPEN" && SEVERITY_TYPES[a.severity] === "ALERT").length;
}

// --- Dates: grouping and filtering the Alertas feed -------------------------------------------
//
// The feed grew a date axis — days as collapsible groups, plus a range filter — and all of its
// arithmetic lives here rather than in the view: it is the part worth pinning with tests, and
// `PanelCierres` already needed half of it.
//
// Everything works on `YYYY-MM-DD` strings and never builds a `Date` from one to compare or
// format it (`fecha_cuadre` is BR-04's business_date; a local `Date` from a date-only string
// shifts the day for anyone west of Greenwich). `restarDias` is the one exception and goes
// through UTC explicitly. Every comparison runs `parseDateOnly` first, because
// `"2026-08-18T00:00:00.000Z" > "2026-08-18"` is lexicographically TRUE — an inclusive `hasta`
// would otherwise exclude the last day of its own range.

// The chips the feed offers, label included, so nothing builds these strings in JSX and
// `etiquetaDeRangoFecha` stays pure. "personalizado" is deliberately absent: it is not a chip,
// it is what typing into a date input means.
const TODAS_LAS_FECHAS = "Todas las fechas";

export const PRESETS_FECHA = [
  { clave: "todo", etiqueta: "Todo" },
  { clave: "hoy", etiqueta: "Hoy" },
  { clave: "7", etiqueta: "7 días" },
  { clave: "30", etiqueta: "30 días" },
];

// One `YYYY-MM-DD` day, `dias` days earlier. Lived privately in `PanelCierres.jsx` until the
// Alertas feed needed it too — for its chips, and to decide what "Ayer" means. Moved here rather
// than copied, so the two views can never disagree about what "7 días" spans.
export function restarDias(fechaISO, dias) {
  const d = new Date(`${fechaISO}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
}

// The range a chip (or a typed range) selects. `hoy` is injected rather than read off the clock
// in here, so the caller owns which timezone's "today" this means — Bogota's, for this app — and
// a test can pin the answer instead of failing next year.
//
// **"7"/"30" close both ends**, the same window `PanelCierres`' `rango(dias)` produces. "7 dias"
// has to mean one thing across the two screens that offer it, and a reader comparing counts
// between them should not find a discrepancy to explain.
//
// The known cost, so nobody rediscovers it as a bug: an alert whose closing is dated AFTER the
// anchor day falls outside every preset, and outside the chip's own count with it. That is
// reachable — `NuevoCierreDialog`'s date input has no `max`, so a closing can be created for a
// future day (an excluded day only warns), and the anchor here is Bogota's today while a date
// built server-side is not, so there is a window each day where the two disagree. Such an alert
// is never lost: "todo" applies no bounds and is the default, so the feed shows it on load and
// only a deliberate preset click hides it.
//
// The six and the twenty-nine are not off-by-ones — "7 dias" is seven calendar days *including
// today*, the same arithmetic `rango(6)` already uses.
//
// An inverted typed range is swapped rather than left to match nothing: somebody who fills
// "hasta" before "desde" would otherwise get a blank page with no explanation. An unknown preset
// or a missing anchor falls back to no bounds at all — on this page every failure has to resolve
// toward showing data.
export function rangoDePreset(preset, hoy, personalizado) {
  if (preset === "personalizado") {
    const desde = parseDateOnly(personalizado?.desde) || "";
    const hasta = parseDateOnly(personalizado?.hasta) || "";
    if (desde && hasta && desde > hasta) return { desde: hasta, hasta: desde };
    return { desde, hasta };
  }
  if (!hoy) return { desde: "", hasta: "" };
  if (preset === "hoy") return { desde: hoy, hasta: hoy };
  if (preset === "7") return { desde: restarDias(hoy, 6), hasta: hoy };
  if (preset === "30") return { desde: restarDias(hoy, 29), hasta: hoy };
  return { desde: "", hasta: "" };
}

// Is a closing's business date inside the filter's range? Both ends optional and inclusive.
//
// With neither end set the range is "todo" and everything passes, including an alert whose
// closing carries no date. Once either end is set, an undated alert drops out: asked for a span
// of days, a row belonging to no day is not in it, and leaving it in would strand a "Sin fecha"
// group inside a result the user asked to be about August. It reappears the moment the filter is
// cleared — which is the default — so nothing is permanently out of reach.
export function enRangoDeFechas(fechaISO, rango) {
  const desde = rango?.desde || "";
  const hasta = rango?.hasta || "";
  if (!desde && !hasta) return true;
  const fecha = parseDateOnly(fechaISO);
  if (!fecha) return false;
  if (desde && fecha < desde) return false;
  if (hasta && fecha > hasta) return false;
  return true;
}

// Is this one of the two days a person names instead of dating? Exists so the view can print the
// real date beside a relative heading without comparing against the strings
// `etiquetaDeGrupoFecha` happens to return today.
export function esFechaRelativa(fechaISO, hoy) {
  if (!fechaISO || !hoy) return false;
  return fechaISO === hoy || fechaISO === restarDias(hoy, 1);
}

// A date group's heading: "Hoy"/"Ayer" for the two days an operator names, the day itself
// otherwise ("18 Ago 2026"). Nothing else is relative — "hace 3 dias" stops being readable past
// two days and makes the reader do arithmetic.
//
// The day is not zero-padded ("1 Sep 2026", not "01"), and the year is always shown: this feed
// routinely mixes a pilot's closings from weeks back with today's cron rows, so a bare "18 Ago"
// would be ambiguous exactly when it matters. `getMonthAbreviation` is 1-based and silently
// answers "Dic" for anything it does not match, so the month goes in as parsed.
export function etiquetaDeGrupoFecha(fechaISO, hoy) {
  if (!fechaISO) return "Sin fecha";
  if (fechaISO === hoy) return "Hoy";
  if (esFechaRelativa(fechaISO, hoy)) return "Ayer";
  const [anio, mes, dia] = fechaISO.split("-");
  return `${Number(dia)} ${getMonthAbreviation(Number(mes))} ${anio}`;
}

// The newest business date among some alerts, or null when none of them has one — so a caller
// with nothing to point at renders no hint rather than "la mas reciente es del —".
export function fechaMasRecienteDeAlertas(alertas) {
  const fechas = (alertas || [])
    .map((a) => parseDateOnly(a?.fechaISO))
    .filter(Boolean)
    .sort();
  return fechas[fechas.length - 1] || null;
}

// What the date filter's trigger says it is doing. Here rather than in JSX because it is the
// piece most likely to be quietly wrong, and this way it is unit-testable.
export function etiquetaDeRangoFecha(preset, rango) {
  if (preset !== "personalizado") {
    const chip = PRESETS_FECHA.find((p) => p.clave === preset);
    // "Todo" is enough on the chip, which sits under a "Filtrar por fecha" heading. On the
    // trigger it sits in a row beside "Todas las prioridades" and "Todas las oficinas", where
    // "Todo" does not say todo *what* — so the unfiltered state names its dimension and the
    // others do not need to ("Hoy" and a date range are unambiguous on their own).
    if (!chip || chip.clave === "todo") return TODAS_LAS_FECHAS;
    return chip.etiqueta;
  }
  const desde = rango?.desde || "";
  const hasta = rango?.hasta || "";
  if (desde && hasta) return `${fechaDMY(desde)} – ${fechaDMY(hasta)}`;
  if (desde) return `Desde ${fechaDMY(desde)}`;
  if (hasta) return `Hasta ${fechaDMY(hasta)}`;
  return TODAS_LAS_FECHAS;
}

// The feed's alerts split into one group per business date, most recent day first.
//
// Order INSIDE a group is the order they arrived in, untouched: the caller has already sorted by
// priority, and re-sorting here would make that sort invisible. This decides only which day an
// alert belongs to and how the days stack — newest first, because the feed is a triage backlog
// and the freshest day is the one being worked.
//
// Alerts whose closing carries no date collect in a single group keyed `null`, always last and
// never dropped: one is still an open alert somebody has to act on, and not hiding one is the
// whole point of this page.
export function agruparAlertasPorFecha(alertas) {
  const porFecha = new Map();
  for (const alerta of alertas || []) {
    const clave = parseDateOnly(alerta?.fechaISO) || null;
    if (!porFecha.has(clave)) porFecha.set(clave, []);
    porFecha.get(clave).push(alerta);
  }
  return [...porFecha.entries()]
    .map(([clave, delDia]) => ({ clave, alertas: delDia }))
    .sort((a, b) => {
      if (a.clave === b.clave) return 0;
      if (!a.clave) return 1;
      if (!b.clave) return -1;
      return a.clave < b.clave ? 1 : -1;
    });
}

// B-46 — the dialog's alert column, most serious first: red (alert, blocked), then subalert,
// then the green confirmations. Stable, so alerts of one severity keep the backend's order.
const ORDEN_SEVERIDAD = { ALERT: 0, SUBALERT: 1, INFO: 2, CARRIED: 3 };

export const ordenarAlertasPorSeveridad = (alertas) => (alertas || [])
  .map((alerta, indice) => ({ alerta, indice }))
  .sort((a, b) => {
    const pa = ORDEN_SEVERIDAD[SEVERITY_TYPES[a.alerta?.severity]] ?? 2;
    const pb = ORDEN_SEVERIDAD[SEVERITY_TYPES[b.alerta?.severity]] ?? 2;
    return pa - pb || a.indice - b.indice;
  })
  .map(({ alerta }) => alerta);

// B-46 — what is actually missing for a closing to agree, in place of "Documentos faltantes".
//
// That section used to list the documents an OPEN alert cites as evidence, so a closing blocked
// only by a figure the bank's informe never sent showed "Cuadre Oficina — Pendiente" beside a
// cuadre that had been uploaded and read: the operator was told a file was missing that was
// sitting right there. The truth is in the calculations: each unresolved one names the inputs it
// lacked (`missingInputs`, `<source>.<field>`). Grouped by source, and each source marked
// loaded or not from the closing's own sources, that says the real thing — "the informe has not
// arrived" or "the cuadre is loaded and does not print X".
//
// `resultados` are `toResultView` rows, `pasos` are `toStepView` rows. A missing input with no
// dot is another calculation, which is not a thing a person supplies, and is skipped.
export const pendientesDelCierre = (resultados, pasos) => {
  const porFuente = new Map();
  (resultados || []).forEach((resultado) => {
    if (resultado?.resolved) return;
    (resultado?.missingInputs || []).forEach((ref) => {
      const punto = String(ref).indexOf(".");
      if (punto <= 0) return;
      const fuente = ref.slice(0, punto);
      const campo = ref.slice(punto + 1);
      if (!porFuente.has(fuente)) porFuente.set(fuente, { campos: new Set(), calculos: new Set() });
      porFuente.get(fuente).campos.add(campo);
      porFuente.get(fuente).calculos.add(resultado.key);
    });
  });

  const cargadas = new Set((pasos || []).filter((p) => p?.ok).map((p) => p.label));
  return [...porFuente.entries()]
    .map(([fuente, { campos, calculos }]) => ({
      fuente,
      nombre: nombreLegibleDeClave(fuente),
      cargado: cargadas.has(fuente),
      campos: [...campos].sort().map((campo) => ({ clave: campo, nombre: nombreLegibleDeClave(campo) })),
      calculos: [...calculos].sort(),
    }))
    // What has not arrived first — that is the one to go and get.
    .sort((a, b) => Number(a.cargado) - Number(b.cargado) || a.nombre.localeCompare(b.nombre));
};

// --- B-56: the operator's observation on an alert ----------------------------------------------
//
// The macro's column AK OBSERVACIONES, which is `closing_alerts.resolution_note` here. Three
// helpers, pure, because what they get wrong is invisible on screen: a timestamp rendered in the
// wrong timezone names the wrong day, and a card that does not take the server's answer keeps
// showing "Abierta" over a row that is already closed.

// A full ISO timestamp (`resolved_at`) as Bogotá reads it: `21-09-2026 10:42`.
//
// NOT `fechaDMY`. That one splits the string and never builds a `Date`, which is exactly right for
// `business_date` and exactly wrong here: `resolved_at` is an instant, stored UTC, so 2026-09-22
// T03:00Z happened at 22:00 on the 21st in Bogotá and splitting the text would print the 22nd. A
// date-only string is handed to `fechaDMY` instead of being turned into a `Date`, for the same
// reason in reverse.
export function fechaHoraBogota(iso) {
  const crudo = String(iso ?? "").trim();
  if (!crudo) return "—";
  if (!crudo.includes("T")) return fechaDMY(crudo);
  const instante = new Date(crudo);
  if (Number.isNaN(instante.getTime())) return crudo;
  const partes = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(instante);
  const parte = (tipo) => partes.find((p) => p.type === tipo)?.value || "";
  return `${parte("day")}-${parte("month")}-${parte("year")} ${parte("hour")}:${parte("minute")}`;
}

// One alert's resolution, in the words `STATUS_LABELS` already uses — `""` while it is open.
//
// `RESOLVED_BY_DATA` and `RESOLVED_BY_USER` stay distinguishable on screen: the figures moved, or
// a person decided, and conflating them would credit the engine's work to whoever is reading.
// A missing author or date drops its own clause rather than printing "por undefined".
//
// The engine signs its own resolutions `evaluation` (`lifecycle.js`'s EVALUATION_ACTOR). That is a
// key, not a name: "Resuelta por los datos por evaluation" read as a typo to the operator (E2E test,
// 29/09/2026). The label already says the data resolved it, so the engine's clause is dropped.
export const AUTOR_DEL_SISTEMA = "evaluation";

// B-116 — resolved by a person's correction: the backend serves who corrected the figure, when and
// why (`correctedBy`, `correctedAt`, `correctionReason`), and that is the attention to show.
export const ESTADO_POR_CORRECCION = "Resuelta por corrección";

export function atencionDeAlerta(alerta) {
  if (!alerta || esAlertaAbierta(alerta)) return "";
  if (alerta.correctedBy) {
    const cuandoCorrigio = alerta.correctedAt ? fechaHoraBogota(alerta.correctedAt) : "";
    const texto = `${ESTADO_POR_CORRECCION} de ${alerta.correctedBy}`;
    return cuandoCorrigio && cuandoCorrigio !== "—" ? `${texto} · ${cuandoCorrigio}` : texto;
  }
  const etiqueta = STATUS_LABELS[alerta.status] || alerta.status || "";
  if (!etiqueta) return "";
  const partes = [etiqueta];
  if (alerta.resolvedBy && alerta.resolvedBy !== AUTOR_DEL_SISTEMA) partes.push(`por ${alerta.resolvedBy}`);
  const cuando = alerta.resolvedAt ? fechaHoraBogota(alerta.resolvedAt) : "";
  return cuando && cuando !== "—" ? `${partes.join(" ")} · ${cuando}` : partes.join(" ");
}

// B-115 — an amount the backend serves ("30000.00", "-1000083.50") the way a person types it under
// es-CO ("30.000", "-1.000.083,50"), to prefill the adjustment box. The backend parses what comes back.
export function montoParaEscribir(valor) {
  const n = numeroDeCelda(valor);
  if (n === null) return "";
  const conCentavos = Math.round(Math.abs(n) * 100) % 100 !== 0;
  return `${n < 0 ? "-" : ""}${formatMoney(Math.abs(n), conCentavos ? 2 : 0)}`;
}

// B-115 — an amount being typed, with its thousands dots put in as it goes ("-330000" -> "-330.000",
// "1234,5" -> "1.234,5"): only digits, one leading "-" and one "," with up to two decimals survive.
// A positive amount needs no sign, so a "+" is dropped. What comes out is what `montoEscrito` reads.
export function montoMientrasSeEscribe(texto) {
  const crudo = String(texto ?? "").replace(/[−–]/g, "-");
  const negativo = crudo.trim().startsWith("-");
  const [entera, ...resto] = crudo.split(",");
  const digitos = entera.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  const agrupados = digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const decimales = resto.length ? `,${resto.join("").replace(/\D/g, "").slice(0, 2)}` : "";
  return `${negativo ? "-" : ""}${agrupados}${decimales}`;
}

// B-115 — the colour of an adjustment: green when it adds to the opening balance, red when it takes
// away, nothing for no amount.
export function tonoDeAjuste(valor) {
  const n = numeroDeCelda(valor);
  if (!n) return "";
  return n > 0 ? "text-brinks-success" : "text-brinks-danger";
}

// B-115 — the adjustment an attended alert made to the next day's opening balance, in words; "" when
// it made none. A positive one carries its "+": the sign is the whole point of the figure.
export function ajusteDeAlerta(alerta) {
  const monto = numeroDeCelda(alerta?.adjustment?.amount);
  if (monto === null) return "";
  return `Ajuste al saldo de bóveda: ${monto > 0 ? "+" : ""}${montoLegible(monto)}`;
}

const DIA_DETENIDO = {
  NOT_PROCESSED: (dia) => `El ${dia} todavía no se ha procesado; tomará el ajuste cuando se procese.`,
  CLOSED: (dia) => `El ${dia} está cerrado; reábrelo y evalúalo para que tome el ajuste.`,
  RUNNING: (dia) => `Un agente está trabajando el ${dia}; tomará el ajuste en su próxima evaluación.`,
  FAILED: (dia) => `No se pudo volver a revisar el ${dia}; evalúalo de nuevo desde su detalle.`,
};

// B-115 — what attending with an adjustment did to the following days, from `resolveCierreAlert`'s
// answer (`followingDays`, `stoppedAt`); "" when it carried no adjustment.
export function resultadoDeAjuste(data) {
  if (!data?.adjustment) return "";
  const revisados = (data.followingDays || []).map((dia) => fechaDMY(dia.businessDate));
  const partes = [
    revisados.length
      ? `Se ajustó el saldo de bóveda y se volvió a revisar ${revisados.length === 1 ? "el" : "los días"} ${revisados.join(", ")}.`
      : "Se guardó el ajuste al saldo de bóveda.",
  ];
  const detenido = data.stoppedAt;
  if (detenido && DIA_DETENIDO[detenido.reason]) partes.push(DIA_DETENIDO[detenido.reason](fechaDMY(detenido.businessDate)));
  return partes.join(" ");
}

// The note the engine writes when it closes an alert, without the ruleset version it names.
//
// `lifecycle.js` writes `Reemplazada en la evaluación bcm-2026.10.2 por: "…".` and two siblings; the
// version is for `cierres:show`, not for the operator, who read it as noise. Rewritten here rather
// than in the stored note, which is append-only history. A person's own note is shown as written.
const NOTAS_DEL_SISTEMA = [
  [/^Reemplazada en la evaluación \S+ por: (.*)$/s, (_, nueva) => `Reemplazada por: ${nueva}`],
  [/^La alerta dejó de dispararse en la evaluación \S+\.$/, () => "La alerta dejó de dispararse con los datos actuales."],
  [/^La regla ya no existe en el ruleset \S+\.$/, () => "La regla de esta alerta ya no se usa."],
];

export function notaDeResolucion(nota) {
  const texto = String(nota ?? "").trim();
  if (!texto) return "";
  for (const [patron, reescribir] of NOTAS_DEL_SISTEMA) {
    const encontrada = texto.match(patron);
    if (encontrada) return reescribir(...encontrada);
  }
  return texto;
}

// Said under the dialog's "Corrección guardada" heading, so none of these repeats it.
const SIN_RECALCULO = {
  NOT_PROCESSED: "El cierre se recalculará cuando se procesen sus reportes.",
  RUNNING: "El cierre se recalculará cuando el agente termine de trabajarlo.",
  NOT_REQUESTED: "El cierre se recalculará en la próxima revisión.",
};

// B-116 — what saving a correction did to the closing, from `overrideCierreDatapoint`'s answers (one
// per field saved): a sentence and, when the closing was recalculated and something is still open,
// the short list of what. A descuadre is said with its own sentence (its amount); the ones that could
// not be checked yet ("No se pudo revisar todavía…", long) are named together in one line.
//
// @param {function} nombrar  ruleKey -> its readable name (the client's names dictionary)
// @returns {{ texto: string, pendientes: string[] }}
export function resultadoDeCorreccion(respuestas, nombrar = nombreLegibleDeClave) {
  const lista = (respuestas || []).filter(Boolean);
  if (!lista.length) return { texto: "", pendientes: [] };
  const evaluadas = lista.filter((r) => r.evaluated);
  if (evaluadas.length) {
    // The last evaluation saw every correction (they serialise on the closing): what it left open.
    const abiertas = evaluadas[evaluadas.length - 1].openAlerts || [];
    if (!abiertas.length) return { texto: "El cierre se recalculó y no le quedan alertas abiertas.", pendientes: [] };
    const sinRevisar = abiertas.filter((a) => a.severity === "blocked").map((a) => nombrar(a.ruleKey));
    const pendientes = [
      ...abiertas.filter((a) => a.severity !== "blocked").map((a) => a.message || nombrar(a.ruleKey)),
      ...(sinRevisar.length ? [`Falta información para revisar: ${sinRevisar.join(", ")}`] : []),
    ];
    return { texto: "El cierre se recalculó, pero sigue pendiente:", pendientes };
  }
  const fallo = lista.find((r) => r.notEvaluated === "FAILED");
  if (fallo) {
    return {
      texto: "No se pudo recalcular el cierre ahora; se recalculará en la próxima revisión.",
      pendientes: [],
    };
  }
  return { texto: SIN_RECALCULO[lista[0].notEvaluated] || "Corrección guardada.", pendientes: [] };
}

// B-116 — an alert's observation: the reason of the person's correction that resolved it, else its
// note as `notaDeResolucion` reads it. The backend's `notifications/matrix.js#observationOf` is the
// same rule.
export function observacionDeAlerta(alerta) {
  const razon = String(alerta?.correctionReason ?? "").trim();
  return razon || notaDeResolucion(alerta?.resolutionNote);
}

// The feed's list with one alert carrying the server's answer — status, author, date and note.
//
// A new array, and the matched element is a new object: the list is React state, and mutating it
// in place is a card that does not repaint. Everything the caller already shaped (`titulo`,
// `oficina`, `fechaISO`, `documentos`) survives, so this replaces a row's *resolution* and nothing
// else, and it is why resolving does not cost the ~10s `filterOffices` + batch fetch again.
export function aplicarResolucion(alertas, resuelta) {
  if (!resuelta?.id) return alertas || [];
  return (alertas || []).map((a) =>
    a.id === resuelta.id
      ? {
        ...a,
        status: resuelta.status,
        resolvedBy: resuelta.resolvedBy ?? null,
        resolvedAt: resuelta.resolvedAt ?? null,
        resolutionNote: resuelta.resolutionNote ?? null,
      }
      : a
  );
}
